import type { EventAttributes, Faro, MetaUser } from '@grafana/faro-web-sdk'

/**
 * Guarded access to the Faro instance for app code (composables, stores).
 *
 * Reachable WITHOUT importing `@grafana/faro-web-sdk` - the type imports above
 * are erased at build time - so a store recording one breadcrumb does not drag
 * the SDK into the server bundle or the entry chunk. `0.telemetry.client.ts`
 * hands the instance over after its dynamic import; until then, during SSR, and
 * when Faro is disabled, every call here is a no-op.
 */
let instance: Faro | undefined

export function setFaroInstance(faro: Faro) {
  instance = faro
}

export function useFaro(): Faro | undefined {
  return instance
}

/**
 * Faro's `Sentry.addBreadcrumb`. Attributes must be a FLAT
 * `Record<string, string>`, so nested objects get dotted keys and leaves are
 * stringified; nullish leaves are dropped, not sent as "null".
 */
export function pushFaroEvent(name: string, attributes?: Record<string, unknown>, domain?: string) {
  const api = instance?.api
  if (!api)
    return

  try {
    api.pushEvent(name, attributes ? flattenAttributes(attributes) : undefined, domain)
  }
  catch {
    // Telemetry never throws into business logic.
  }
}

export function setFaroUser(user: MetaUser) {
  try {
    instance?.api.setUser(user)
  }
  catch {}
}

/**
 * The closest thing Faro has to `Sentry.setTag`. `setSession` REPLACES the
 * session meta, so the current session (id included) and its existing
 * attributes must be spread back in or they are silently dropped.
 */
export function setFaroSessionAttributes(attributes: Record<string, string>) {
  const api = instance?.api
  if (!api)
    return

  try {
    const session = api.getSession() ?? {}
    api.setSession({ ...session, attributes: { ...session.attributes, ...attributes } })
  }
  catch {}
}

function flattenAttributes(input: Record<string, unknown>, prefix = '', out: EventAttributes = {}): EventAttributes {
  for (const [key, value] of Object.entries(input)) {
    const path = prefix ? `${prefix}.${key}` : key

    if (value == null)
      continue

    if (Array.isArray(value))
      out[path] = JSON.stringify(value)
    else if (typeof value === 'object')
      flattenAttributes(value as Record<string, unknown>, path, out)
    else
      out[path] = String(value)
  }

  return out
}
